import { createFileRoute } from '@tanstack/react-router';
import { SECTION_ORDER } from '@/components/reports/report-shared';

// ENT-466 T00 (reports-url-routing) — `pageView` (and, for a historical run, `runId`/`origin`)
// used to live only in ReportsPage.tsx's component state, invisible to the router: browser Back
// from Recent reports exited /reports entirely, a reload always dropped back to the wizard, and a
// completed run could not be deep-linked or refreshed. Search params (not nested routes) match
// the established local convention (clients/$clientId.tsx's ?tab=) and, structurally, don't risk
// unmounting ReportsPage on a view change — the wizard-state-preservation invariant T05 relies on.
//
// Values are user-editable/untrusted input (same discipline as agreements/$agreementId.tsx) — every
// field is narrowed to a closed shape here, nothing passes through raw.
export const Route = createFileRoute('/reports')({
  // ENT-466 EW-T06 (FR-1.5) — catalogue discovery state (search + both filters + section) is
  // URL-backed too, so a link, a reload, and browser Back/Forward all reproduce the same result
  // set (AC-38). Same discipline as the fields above: every value is untrusted input and is
  // narrowed to a closed shape here, never passed through raw. An unrecognised value degrades to
  // undefined (= no filter) rather than throwing or filtering the catalogue to nothing.
  validateSearch: (search: Record<string, unknown>): {
    view?: 'recent' | 'saved' | 'run'
    runId?: string
    origin?: 'recent' | 'saved'
    q?: string
    scope?: 'ALL_ACTIVE' | 'SELECTED'
    format?: 'CSV' | 'XLSX'
    section?: (typeof SECTION_ORDER)[number]
  } => ({
    // Empty/whitespace-only search collapses to undefined so the router omits the key entirely
    // rather than serialising a meaningless `?q=`.
    q: typeof search.q === 'string' && search.q.trim() ? search.q : undefined,
    scope:
      search.scope === 'ALL_ACTIVE' || search.scope === 'SELECTED' ? search.scope : undefined,
    format: search.format === 'CSV' || search.format === 'XLSX' ? search.format : undefined,
    section: SECTION_ORDER.includes(search.section as (typeof SECTION_ORDER)[number])
      ? (search.section as (typeof SECTION_ORDER)[number])
      : undefined,
    view:
      search.view === 'recent' || search.view === 'saved' || search.view === 'run'
        ? search.view
        : undefined,
    runId: typeof search.runId === 'string' ? search.runId : undefined,
    // ENT-466-POSTCLEANUP T-A — `origin` is ONLY load-bearing for `view=run`'s Back button
    // (ReportsPage.tsx's `onBack={() => navigate({ search: { view: runViewOrigin } })}`), never
    // consumed anywhere else. Defaulting it unconditionally (as this used to) meant TanStack
    // Router serialized it onto EVERY /reports URL, including bare `/reports` and
    // `?view=recent`/`?view=saved`, where it carries no meaning. Scoping the default to
    // `view === 'run'` — same 'recent' fallback as before, just conditional on where it matters —
    // means the router's default serializer omits the key entirely everywhere else.
    origin:
      search.view === 'run'
        ? search.origin === 'recent' || search.origin === 'saved'
          ? search.origin
          : 'recent'
        : undefined,
  }),
});
